import React, { useState, useEffect } from 'react';
import { FaTimes, FaUser, FaEdit, FaSave, FaSpinner, FaPhone, FaEnvelope, FaMapMarkerAlt, FaStar, FaBriefcase, FaCalendarAlt, FaIdCard, FaShieldAlt, FaBell, FaLocationArrow, FaMobile } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { driverService } from '../../api/driverService';

const DriverDetailsModal = ({ isOpen, onClose, driverId, onDriverUpdated, initialDriverData }) => {
  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});

  console.log('DriverDetailsModal props:', { isOpen, driverId, onClose, onDriverUpdated, initialDriverData });

  useEffect(() => {
    console.log('useEffect triggered - isOpen:', isOpen, 'driverId:', driverId);
    if (isOpen && driverId) {
      console.log('Fetching driver details...');
      // If we have initial data, use it immediately
      if (initialDriverData) {
        console.log('Using initial driver data:', initialDriverData);
        setDriver(initialDriverData);
        setFormData({
          firstName: initialDriverData.firstName || '',
          lastName: initialDriverData.lastName || '',
          email: initialDriverData.email || '',
          phoneNumber: initialDriverData.phoneNumber || initialDriverData.phone || '',
          status: initialDriverData.status || 'active',
          notificationPreferences: initialDriverData.notificationPreferences || {}
        });
        setLoading(false);
      }
      // Still fetch fresh data from API
      fetchDriverDetails();
    }
  }, [isOpen, driverId, initialDriverData]);

  const fetchDriverDetails = async () => {
    try {
      setLoading(true);
      console.log('Fetching driver details for ID:', driverId);
      
      // Test the API call
      const response = await driverService.getDriverById(driverId);
      console.log('Full API Response:', response);
      console.log('Response type:', typeof response);
      console.log('Response keys:', Object.keys(response));
      
      if (response.success) {
        // The API returns data in format: { success: true, data: { driver: {...}, recentJobs: [...], earnings: [...] } }
        const driverData = response.data.driver;
        console.log('Driver Data:', driverData);
        
        if (!driverData) {
          console.error('No driver data found in response');
          console.log('Response.data:', response.data);
          toast.error('Driver data not found in response');
          return;
        }
        
        setDriver(driverData);
        setFormData({
          firstName: driverData.firstName || '',
          lastName: driverData.lastName || '',
          email: driverData.email || '',
          phoneNumber: driverData.phoneNumber || driverData.phone || '',
          status: driverData.status || 'active',
          notificationPreferences: driverData.notificationPreferences || {}
        });
      } else {
        console.error('API returned error:', response.message);
        toast.error(response.message || 'Failed to load driver details');
      }
    } catch (error) {
      console.error('Error fetching driver details:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Failed to load driver details');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNotificationChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      notificationPreferences: {
        ...prev.notificationPreferences,
        [key]: value
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await driverService.updateDriver(driverId, formData);
      
      if (response.success) {
        setDriver(response.data);
        setEditing(false);
        toast.success('Driver updated successfully');
        if (onDriverUpdated) {
          onDriverUpdated(response.data);
        }
      } else {
        toast.error(response.message || 'Failed to update driver');
      }
    } catch (error) {
      console.error('Error updating driver:', error);
      toast.error('Failed to update driver');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setSaving(true);
      let response;
      
      switch (newStatus) {
        case 'active':
          response = await driverService.activateDriver(driverId);
          break;
        case 'suspended':
          response = await driverService.suspendDriver(driverId);
          break;
        case 'inactive':
          response = await driverService.deactivateDriver(driverId);
          break;
        default:
          response = await driverService.updateDriver(driverId, { status: newStatus });
      }
      
      if (response.success) {
        setDriver(response.data);
        setFormData(prev => ({ ...prev, status: newStatus }));
        toast.success(`Driver status updated to ${newStatus}`);
        if (onDriverUpdated) {
          onDriverUpdated(response.data);
        }
      } else {
        toast.error(response.message || 'Failed to update driver status');
      }
    } catch (error) {
      console.error('Error updating driver status:', error);
      toast.error('Failed to update driver status');
    } finally {
      setSaving(false);
    }
  };

  // Helper function to convert Firestore timestamp to readable date
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    if (timestamp._seconds) {
      // Firestore timestamp format
      return new Date(timestamp._seconds * 1000).toLocaleString();
    } else if (timestamp.toDate) {
      // Firestore timestamp object
      return timestamp.toDate().toLocaleString();
    } else {
      // ISO string or Date object
      return new Date(timestamp).toLocaleString();
    }
  };

  // Helper function to format location data
  const formatLocation = (location) => {
    if (!location) return 'N/A';
    return `${location.latitude?.toFixed(6)}, ${location.longitude?.toFixed(6)}`;
  };

  if (!isOpen || !driverId) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg">
              <FaUser className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Driver Details</h2>
              <p className="text-blue-100">
                {driver ? `${driver.firstName} ${driver.lastName}` : 'Loading...'}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {editing ? (
              <>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center"
                >
                  {saving ? <FaSpinner className="animate-spin mr-2" /> : <FaSave className="mr-2" />}
                  Save
                </button>
                <button
                  onClick={() => {
                    setEditing(false);
                    setFormData({
                      firstName: driver?.firstName || '',
                      lastName: driver?.lastName || '',
                      email: driver?.email || '',
                      phoneNumber: driver?.phoneNumber || driver?.phone || '',
                      status: driver?.status || 'active',
                      notificationPreferences: driver?.notificationPreferences || {}
                    });
                  }}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setEditing(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                >
                  <FaEdit className="mr-2" />
                  Edit
                </button>
                <button
                  onClick={fetchDriverDetails}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 flex items-center ml-2"
                >
                  <FaSpinner className="mr-2" />
                  Refresh
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="text-white hover:text-blue-200 transition-colors ml-2"
            >
              <FaTimes className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <FaSpinner className="animate-spin h-8 w-8 text-blue-500" />
              <span className="ml-2 text-gray-600">Loading driver details...</span>
            </div>
          ) : driver ? (
            <>
              {/* Profile Section */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-start space-x-6">
                  {/* Profile Picture */}
                  <div className="flex-shrink-0">
                    {driver.profileImageUrl ? (
                      <img
                        src={driver.profileImageUrl}
                        alt={`${driver.firstName} ${driver.lastName}`}
                        className="h-24 w-24 rounded-full object-cover border-4 border-white shadow-lg"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className={`h-24 w-24 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-2xl font-bold shadow-lg ${driver.profileImageUrl ? 'hidden' : 'flex'}`}>
                      {driver.firstName?.[0] || 'D'}{driver.lastName?.[0] || 'R'}
                    </div>
                  </div>
                  
                  {/* Basic Info */}
                  <div className="flex-1">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                        {editing ? (
                          <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          />
                        ) : (
                          <p className="text-lg font-semibold text-gray-900">{driver.firstName || 'N/A'}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                        {editing ? (
                          <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          />
                        ) : (
                          <p className="text-lg font-semibold text-gray-900">{driver.lastName || 'N/A'}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Driver License Information */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <FaIdCard className="mr-2 text-indigo-500" />
                  Driver License Information
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* License Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">License Number</label>
                    <p className="text-gray-900 font-mono text-lg">{driver.licenseNumber || 'N/A'}</p>
                  </div>
                  
                  {/* License Image */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">License Photo</label>
                    {driver.licenseImageUrl ? (
                      <div className="relative">
                        <img
                          src={driver.licenseImageUrl}
                          alt="Driver License"
                          className="w-full max-w-md h-64 object-cover rounded-lg border border-gray-300 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                          onClick={() => window.open(driver.licenseImageUrl, '_blank')}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="hidden w-full max-w-md h-64 bg-gray-100 rounded-lg border border-gray-300 flex items-center justify-center">
                          <div className="text-center text-gray-500">
                            <FaIdCard className="mx-auto h-12 w-12 mb-2" />
                            <p className="text-sm">License image failed to load</p>
                          </div>
                        </div>
                        <div className="mt-2 text-xs text-gray-500 text-center">
                          Click image to view full size
                        </div>
                      </div>
                    ) : (
                      <div className="w-full max-w-md h-64 bg-gray-100 rounded-lg border border-gray-300 flex items-center justify-center">
                        <div className="text-center text-gray-500">
                          <FaIdCard className="mx-auto h-12 w-12 mb-2" />
                          <p className="text-sm">No license image available</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <FaPhone className="mr-2 text-blue-500" />
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    {editing ? (
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      />
                    ) : (
                      <p className="text-gray-900">{driver.email || 'N/A'}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    {editing ? (
                      <input
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      />
                    ) : (
                      <p className="text-gray-900">{driver.phoneNumber || driver.phone || 'N/A'}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Driver Status & Performance */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <FaShieldAlt className="mr-2 text-green-500" />
                  Status & Performance
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    {editing ? (
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="suspended">Suspended</option>
                      </select>
                    ) : (
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        driver.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : driver.status === 'inactive'
                          ? 'bg-gray-100 text-gray-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {driver.status}
                      </span>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                    <p className="text-gray-900 capitalize">{driver.role || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Active Job</label>
                    <p className="text-gray-900">{driver.activeJobId ? 'Yes' : 'No'}</p>
                  </div>
                </div>
              </div>

              {/* Location Information */}
              {driver.lastKnownLocation && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <FaLocationArrow className="mr-2 text-purple-500" />
                    Last Known Location
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Coordinates</label>
                      <p className="text-gray-900 font-mono text-sm">{formatLocation(driver.lastKnownLocation)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Updated</label>
                      <p className="text-gray-900">{formatTimestamp(driver.lastKnownLocation.updatedAt)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Accuracy</label>
                      <p className="text-gray-900">{driver.lastKnownLocation.accuracy ? `${driver.lastKnownLocation.accuracy}m` : 'N/A'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Notification Preferences */}
              {driver.notificationPreferences && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <FaBell className="mr-2 text-orange-500" />
                    Notification Preferences
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="generalNotifications"
                        checked={driver.notificationPreferences.generalNotifications || false}
                        disabled={!editing}
                        onChange={(e) => handleNotificationChange('generalNotifications', e.target.checked)}
                        className="mr-2"
                      />
                      <label htmlFor="generalNotifications" className="text-sm text-gray-700">General Notifications</label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="newJobs"
                        checked={driver.notificationPreferences.newJobs || false}
                        disabled={!editing}
                        onChange={(e) => handleNotificationChange('newJobs', e.target.checked)}
                        className="mr-2"
                      />
                      <label htmlFor="newJobs" className="text-sm text-gray-700">New Jobs</label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="deliveryConfirmations"
                        checked={driver.notificationPreferences.deliveryConfirmations || false}
                        disabled={!editing}
                        onChange={(e) => handleNotificationChange('deliveryConfirmations', e.target.checked)}
                        className="mr-2"
                      />
                      <label htmlFor="deliveryConfirmations" className="text-sm text-gray-700">Delivery Confirmations</label>
                    </div>
                  </div>
                </div>
              )}

              {/* Account Information */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <FaCalendarAlt className="mr-2 text-orange-500" />
                  Account Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Driver ID</label>
                    <p className="text-gray-900 font-mono text-sm">{driver.id}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Join Date</label>
                    <p className="text-gray-900">{formatTimestamp(driver.createdAt)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Updated</label>
                    <p className="text-gray-900">{formatTimestamp(driver.updatedAt)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">FCM Token Updated</label>
                    <p className="text-gray-900">{formatTimestamp(driver.fcmTokenUpdatedAt)}</p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <FaUser className="mx-auto h-12 w-12 text-gray-400" />
              <p className="text-gray-500 mt-2">Driver not found</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DriverDetailsModal;
